import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';
import { StateStorage } from 'zustand/middleware';

/**
 * Creates a storage adapter that uses localforage on web and AsyncStorage on native
 * 
 * localforage automatically uses IndexedDB on web (with fallbacks to WebSQL/localStorage)
 * which provides much larger storage capacity than localStorage/AsyncStorage
 * 
 * On React Native, we use AsyncStorage directly
 */
export function createPlatformStorage(): StateStorage {
  // On native platforms, use AsyncStorage
  if (Platform.OS !== 'web') {
    return {
      getItem: async (name: string): Promise<string | null> => {
        return await AsyncStorage.getItem(name);
      },
      setItem: async (name: string, value: string): Promise<void> => {
        await AsyncStorage.setItem(name, value);
      },
      removeItem: async (name: string): Promise<void> => {
        await AsyncStorage.removeItem(name);
      },
    };
  }

  // On web, dynamically import localforage (web-only library)
  // This prevents it from being bundled in React Native builds
  // Check that we're actually in a browser environment
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    // Fallback to a no-op storage if window/document aren't available
    // (can happen during SSR or build time)
    return {
      getItem: async () => null,
      setItem: async () => {},
      removeItem: async () => {},
    };
  }

  // Check if any storage APIs are available
  const hasIndexedDB = typeof (window as any).indexedDB !== 'undefined';
  const hasLocalStorage = typeof window.localStorage !== 'undefined';
  
  if (!hasIndexedDB && !hasLocalStorage) {
    // No storage APIs available - fallback to no-op
    return {
      getItem: async () => null,
      setItem: async () => {},
      removeItem: async () => {},
    };
  }

  type LocalForageInstance = {
    getItem: (key: string) => Promise<any>;
    setItem: (key: string, value: any) => Promise<any>;
    removeItem: (key: string) => Promise<void>;
    ready: () => Promise<void>;
    setDriver: (drivers: string[]) => Promise<void>;
    driver: () => string;
  };

  let localforageInstance: LocalForageInstance | null = null;
  let localforagePromise: Promise<LocalForageInstance> | null = null;

  const getLocalForage = async (): Promise<LocalForageInstance> => {
    if (localforageInstance) {
      return localforageInstance;
    }

    if (localforagePromise) {
      return localforagePromise;
    }

    localforagePromise = import('localforage').then(async (module) => {
      const localforage = module.default;
      
      // Build driver array based on availability
      // Only include drivers that are actually available
      const availableDrivers: string[] = [];
      if (hasIndexedDB && localforage.INDEXEDDB) {
        availableDrivers.push(localforage.INDEXEDDB);
      }
      // Note: WebSQL is deprecated but still exists in some browsers
      // We'll let localforage handle availability checking
      if (localforage.WEBSQL) {
        availableDrivers.push(localforage.WEBSQL);
      }
      if (hasLocalStorage && localforage.LOCALSTORAGE) {
        availableDrivers.push(localforage.LOCALSTORAGE);
      }

      if (availableDrivers.length === 0) {
        throw new Error('No available storage drivers found. IndexedDB and localStorage are not available.');
      }

      // Use createInstance to create a configured instance
      // createInstance will automatically try to use the first available driver from the list
      const instance = localforage.createInstance({
        name: 'votesnow-storage',
        version: 1.0,
        storeName: 'zustand-store',
        driver: availableDrivers,
      });
      
      // Ensure driver is ready before using
      // This waits for the storage backend to be initialized
      // ready() will throw if no driver can be initialized
      await instance.ready();
      
      localforageInstance = instance;
      return instance;
    });

    return localforagePromise;
  };

  return {
    getItem: async (name: string): Promise<string | null> => {
      try {
        const localforage = await getLocalForage();
        const value = (await localforage.getItem(name)) as string | null;
        return value ?? null;
      } catch (error) {
        console.error('Error reading from localforage:', error);
        return null;
      }
    },
    setItem: async (name: string, value: string): Promise<void> => {
      const localforage = await getLocalForage();
      await localforage.setItem(name, value);
    },
    removeItem: async (name: string): Promise<void> => {
      const localforage = await getLocalForage();
      await localforage.removeItem(name);
    },
  };
}

